import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHistory,
    faSearch,
    faChevronLeft,
    faChevronRight,
    faFilePdf,
    faFileExcel,
    faFileCsv,
    faUpload,
    faRotateLeft,
    faMoneyBillTransfer,
} from "@fortawesome/free-solid-svg-icons";
import {
    Card,
    Row,
    Col,
    Table,
    Badge,
    Button,
    Form,
    InputGroup,
} from "@themesberg/react-bootstrap";

import {
    adminRefundSettlements,
    adminImportRefundSettlements,
    adminExportRefundSettlements,
    adminCommissionSettlements,
    adminImportCommissionSettlements,
    adminExportCommissionSettlements,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
    background: "#1D4E89",
    border: "none",
    borderRadius: "0.9rem",
    color: "#ffffff",
};

const INACTIVE_BUTTON_STYLE = {
    background: "#ffffff",
    border: "1px solid #d8e1f0",
    borderRadius: "0.9rem",
    color: "#1f2a44",
};

const DEFAULT_PAGINATION = {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
};

const badgeVariant = (status) => {
    const value = String(status || "").toLowerCase();

    if (
        [
            "processed",
            "success",
            "successful",
            "completed",
            "paid",
            "settled",
        ].includes(value)
    ) {
        return "success";
    }

    if (
        [
            "pending",
            "processing",
            "initiated",
        ].includes(value)
    ) {
        return "warning";
    }

    if (
        [
            "failed",
            "rejected",
            "cancelled",
            "canceled",
        ].includes(value)
    ) {
        return "danger";
    }

    return "secondary";
};

const formatDate = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") {
        return "N/A";
    }

    return value;
};

const getPageItems = (currentPage, totalPages) => {
    const current = Number(currentPage || 1);
    const total = Number(totalPages || 1);

    if (total <= 5) {
        return Array.from({ length: total }, (_, index) => index + 1);
    }

    if (current <= 2) {
        return [1, 2, 3, "...", total];
    }

    if (current >= total - 1) {
        return [
            1,
            "...",
            total - 2,
            total - 1,
            total,
        ];
    }

    return [
        1,
        "...",
        current - 1,
        current,
        current + 1,
        "...",
        total,
    ];
};

const getShowingRange = (pagination, currentRowsCount = 0) => {
    const total = Number(pagination?.total || 0);
    const perPage = Number(pagination?.per_page || 10);

    const lastPage = Math.max(
        1,
        Number(
            pagination?.last_page ||
            Math.ceil(total / perPage) ||
            1
        )
    );

    const current = Math.min(
        Math.max(1, Number(pagination?.current_page || 1)),
        lastPage
    );

    if (total === 0 || currentRowsCount === 0) {
        return {
            start: 0,
            end: 0,
        };
    }

    const start = (current - 1) * perPage + 1;

    const end = Math.min(
        start + currentRowsCount - 1,
        total
    );

    return {
        start: Math.min(start, total),
        end: Math.max(
            Math.min(end, total),
            Math.min(start, total)
        ),
    };
};

const getApiData = (response) => {
    return response?.data?.data || response?.data || {};
};

const getSettlementsFromResponse = (response) => {
    const apiData = getApiData(response);

    return Array.isArray(apiData?.settlements)
        ? apiData.settlements
        : [];
};

const getPaginationFromResponse = (
    response,
    currentPage = 1,
    perPage = 10,
    rowsCount = 0
) => {
    const apiData = getApiData(response);

    return (
        apiData?.pagination || {
            current_page: currentPage,
            last_page: 1,
            per_page: perPage,
            total: rowsCount,
        }
    );
};

export default function AdminSettlementHistory() {
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [activeView, setActiveView] = useState("commission");

    const [search, setSearch] = useState("");

    const [commissionPage, setCommissionPage] = useState(1);
    const [refundPage, setRefundPage] = useState(1);

    const [commissionSettlements, setCommissionSettlements] = useState([]);
    const [refundSettlements, setRefundSettlements] = useState([]);

    const [commissionPagination, setCommissionPagination] = useState(
        DEFAULT_PAGINATION
    );

    const [refundPagination, setRefundPagination] = useState(
        DEFAULT_PAGINATION
    );

    const searchDebounceRef = useRef(null);

    const clearSearchDebounce = () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
    };

    /*
     * Fetch Commission Settlements
     */
    const fetchCommissionSettlements = useCallback(
        async ({
            pageNo = 1,
            perPage = 10,
            searchValue = "",
        } = {}) => {
            try {
                setLoading(true);

                const params = {
                    page: pageNo,
                    per_page: perPage,
                };

                if (String(searchValue || "").trim()) {
                    params.search = String(searchValue).trim();
                }

                const response = await adminCommissionSettlements(params);

                const settlements =
                    getSettlementsFromResponse(response);

                const pagination =
                    getPaginationFromResponse(
                        response,
                        pageNo,
                        perPage,
                        settlements.length
                    );

                setCommissionSettlements(settlements);
                setCommissionPagination(pagination);
            } catch (error) {
                console.error(
                    "Failed to fetch commission settlements:",
                    error
                );

                toast.error(
                    error?.response?.data?.message ||
                    "Unable to load commission settlements."
                );

                setCommissionSettlements([]);

                setCommissionPagination(
                    DEFAULT_PAGINATION
                );
            } finally {
                setLoading(false);
            }
        },
        []
    );

    /*
     * Fetch Refund Settlements
     */
    const fetchRefundSettlements = useCallback(
        async ({
            pageNo = 1,
            perPage = 10,
            searchValue = "",
        } = {}) => {
            try {
                setLoading(true);

                const params = {
                    page: pageNo,
                    per_page: perPage,
                };

                if (String(searchValue || "").trim()) {
                    params.search = String(searchValue).trim();
                }

                const response = await adminRefundSettlements(params);

                const settlements =
                    getSettlementsFromResponse(response);

                const pagination =
                    getPaginationFromResponse(
                        response,
                        pageNo,
                        perPage,
                        settlements.length
                    );

                setRefundSettlements(settlements);
                setRefundPagination(pagination);
            } catch (error) {
                console.error(
                    "Failed to fetch refund settlements:",
                    error
                );

                toast.error(
                    error?.response?.data?.message ||
                    "Unable to load refund settlements."
                );

                setRefundSettlements([]);

                setRefundPagination(
                    DEFAULT_PAGINATION
                );
            } finally {
                setLoading(false);
            }
        },
        []
    );

    /*
     * Initial API call
     */
    useEffect(() => {
        fetchCommissionSettlements({
            pageNo: 1,
            perPage: 10,
            searchValue: "",
        });
    }, [fetchCommissionSettlements]);

    /*
     * Fetch data when active tab,
     * page or search changes
     */
    useEffect(() => {
        clearSearchDebounce();

        const currentPage =
            activeView === "commission"
                ? commissionPage
                : refundPage;

        const currentPerPage =
            activeView === "commission"
                ? commissionPagination?.per_page || 10
                : refundPagination?.per_page || 10;

        const delay =
            String(search || "").trim()
                ? 300
                : 0;

        searchDebounceRef.current = setTimeout(() => {
            if (activeView === "commission") {
                fetchCommissionSettlements({
                    pageNo: currentPage,
                    perPage: currentPerPage,
                    searchValue: search,
                });
            } else {
                fetchRefundSettlements({
                    pageNo: currentPage,
                    perPage: currentPerPage,
                    searchValue: search,
                });
            }
        }, delay);

        return () => {
            clearSearchDebounce();
        };
    }, [
        activeView,
        commissionPage,
        refundPage,
        search,
        commissionPagination?.per_page,
        refundPagination?.per_page,
        fetchCommissionSettlements,
        fetchRefundSettlements,
    ]);

    /*
     * Search
     */
    const handleSearchChange = (value) => {
        setSearch(value);

        setCommissionPage(1);
        setRefundPage(1);
    };

    /*
     * Change Settlement Type
     */
    const handleViewChange = (view) => {
        setActiveView(view);

        setSearch("");

        if (view === "commission") {
            setCommissionPage(1);
        } else {
            setRefundPage(1);
        }
    };

    /*
     * Import Settlement File
     */
    const handleImport = async (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        try {
            setImporting(true);

            const formData = new FormData();

            /*
             * The backend import APIs expect multipart/form-data.
             * The file field is sent as "file".
             */
            formData.append("file", file);

            if (activeView === "commission") {
                await adminImportCommissionSettlements(
                    formData
                );
            } else {
                await adminImportRefundSettlements(
                    formData
                );
            }

            toast.success(
                activeView === "commission"
                    ? "Commission settlements imported successfully."
                    : "Refund settlements imported successfully."
            );

            const currentPage =
                activeView === "commission"
                    ? commissionPage
                    : refundPage;

            const currentPerPage =
                activeView === "commission"
                    ? commissionPagination?.per_page || 10
                    : refundPagination?.per_page || 10;

            if (activeView === "commission") {
                await fetchCommissionSettlements({
                    pageNo: currentPage,
                    perPage: currentPerPage,
                    searchValue: search,
                });
            } else {
                await fetchRefundSettlements({
                    pageNo: currentPage,
                    perPage: currentPerPage,
                    searchValue: search,
                });
            }
        } catch (error) {
            console.error(
                "Failed to import settlements:",
                error
            );

            toast.error(
                error?.response?.data?.message ||
                "Unable to import settlements."
            );
        } finally {
            setImporting(false);

            /*
             * Reset input so the same file can be
             * selected and uploaded again if required.
             */
            event.target.value = "";
        }
    };

    /*
     * Export Settlement Data
     */
    const handleExport = async (format) => {
        try {
            setExporting(true);

            let response;

            if (activeView === "commission") {
                response =
                    await adminExportCommissionSettlements(
                        format
                    );
            } else {
                response =
                    await adminExportRefundSettlements(
                        format
                    );
            }

            const blob = new Blob(
                [response.data],
                {
                    type:
                        response?.headers?.["content-type"] ||
                        "application/octet-stream",
                }
            );

            const url =
                window.URL.createObjectURL(blob);

            const link =
                document.createElement("a");

            link.href = url;

            const extension =
                String(format).toLowerCase() === "excel"
                    ? "xlsx"
                    : String(format).toLowerCase();

            const settlementType =
                activeView === "commission"
                    ? "commission-settlements"
                    : "refund-settlements";

            link.download =
                `${settlementType}.${extension}`;

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);

            toast.success(
                `${activeView === "commission"
                    ? "Commission"
                    : "Refund"
                } settlements exported successfully.`
            );
        } catch (error) {
            console.error(
                "Failed to export settlements:",
                error
            );

            toast.error(
                error?.response?.data?.message ||
                "Unable to export settlements."
            );
        } finally {
            setExporting(false);
        }
    };

    const activePagination =
        activeView === "commission"
            ? commissionPagination
            : refundPagination;

    const activePage =
        activeView === "commission"
            ? commissionPage
            : refundPage;

    const activeRows =
        activeView === "commission"
            ? commissionSettlements
            : refundSettlements;

    const showingRange =
        getShowingRange(
            activePagination,
            activeRows.length
        );

    const pageItems =
        getPageItems(
            activePagination?.current_page || activePage || 1,
            activePagination?.last_page || 1
        );

    /*
     * Pagination handlers
     */
    const handlePreviousPage = () => {
        const currentPage =
            Number(
                activePagination?.current_page ||
                activePage ||
                1
            );

        const nextPage =
            Math.max(1, currentPage - 1);

        if (activeView === "commission") {
            setCommissionPage(nextPage);
        } else {
            setRefundPage(nextPage);
        }
    };

    const handleNextPage = () => {
        const currentPage =
            Number(
                activePagination?.current_page ||
                activePage ||
                1
            );

        const lastPage =
            Number(
                activePagination?.last_page || 1
            );

        const nextPage =
            Math.min(
                lastPage,
                currentPage + 1
            );

        if (activeView === "commission") {
            setCommissionPage(nextPage);
        } else {
            setRefundPage(nextPage);
        }
    };

    const handlePageChange = (page) => {
        if (activeView === "commission") {
            setCommissionPage(page);
        } else {
            setRefundPage(page);
        }
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-4">
                <div>
                    <h2 className="h4 mb-1">
                        Settlement History
                    </h2>
                </div>

                <div className="d-flex justify-content-end ms-auto settlement-toggle-group">
                    <Button
                        type="button"
                        style={
                            activeView === "commission"
                                ? ACTIVE_BUTTON_STYLE
                                : INACTIVE_BUTTON_STYLE
                        }
                        className="fw-semibold settlement-toggle-btn"
                        onClick={() =>
                            handleViewChange("commission")
                        }
                    >
                        <FontAwesomeIcon
                            icon={faMoneyBillTransfer}
                            className="me-2"
                        />
                        Commission
                    </Button>

                    <Button
                        type="button"
                        style={
                            activeView === "refund"
                                ? ACTIVE_BUTTON_STYLE
                                : INACTIVE_BUTTON_STYLE
                        }
                        className="fw-semibold settlement-toggle-btn"
                        onClick={() =>
                            handleViewChange("refund")
                        }
                    >
                        <FontAwesomeIcon
                            icon={faRotateLeft}
                            className="me-2"
                        />
                        Refund
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm rounded-4 mb-4">
                <Card.Body className="p-4">
                    <div className="d-flex flex-column align-items-end">
                        {/* Export Buttons - Top Right */}
                        {/* <div className="admin-settlement-export-group">
                            <Button
                                type="button"
                                variant="outline-primary"
                                className="fw-semibold"
                                disabled={exporting}
                                onClick={() =>
                                    handleExport("csv")
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faFileCsv}
                                    className="me-2"
                                />
                                CSV
                            </Button>

                            <Button
                                type="button"
                                variant="outline-primary"
                                className="fw-semibold"
                                disabled={exporting}
                                onClick={() =>
                                    handleExport("excel")
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faFileExcel}
                                    className="me-2"
                                />
                                Excel
                            </Button>

                            <Button
                                type="button"
                                variant="outline-primary"
                                className="fw-semibold"
                                disabled={exporting}
                                onClick={() =>
                                    handleExport("pdf")
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faFilePdf}
                                    className="me-2"
                                />
                                PDF
                            </Button>
                        </div> */}

                        {/* Import and Search - Below Export Buttons, Right Side */}
                        <div className="admin-settlement-import-search-row">
                            <div className="admin-settlement-search">
                                <InputGroup>
                                    <InputGroup.Text className="bg-white">
                                        <FontAwesomeIcon
                                            icon={faSearch}
                                            className="text-muted"
                                        />
                                    </InputGroup.Text>

                                    <Form.Control
                                        value={search}
                                        onChange={(e) =>
                                            handleSearchChange(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Search"
                                    />
                                </InputGroup>
                            </div>

                            <div className="admin-settlement-import-group">
                                <Form.Label
                                    htmlFor="settlement-import-file"
                                    className="mb-0"
                                >
                                    <span className="btn btn-outline-primary fw-semibold">
                                        <FontAwesomeIcon
                                            icon={faUpload}
                                        // className="me-2"
                                        />
                                        {/* {importing
                                            ? "Importing..."
                                            : "Import"} */}
                                    </span>
                                </Form.Label>

                                <Form.Control
                                    id="settlement-import-file"
                                    type="file"
                                    accept=".csv,.xlsx,.xls,.pdf"
                                    className="d-none"
                                    disabled={importing}
                                    onChange={handleImport}
                                />

                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    disabled={exporting}
                                    onClick={() =>
                                        handleExport("excel")
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faFileExcel}
                                    // className="me-2"
                                    />
                                    {/* Excel */}
                                </Button>

                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    disabled={exporting}
                                    onClick={() =>
                                        handleExport("pdf")
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faFilePdf}
                                    // className="me-2"
                                    />
                                    {/* PDF */}
                                </Button>
                            </div>





                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4">
                <Card.Body className="p-3 p-md-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div>
                            <h5 className="mb-1">
                                {activeView === "commission"
                                    ? "Commission Settlements"
                                    : "Refund Settlements"}
                            </h5>

                            <div className="text-muted small">
                                {activeView === "commission"
                                    ? "Commission settlement records are shown below."
                                    : "Refund settlement records are shown below."}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <Table
                            hover
                            className="align-middle mb-0 settlement-table"
                        >
                            <thead>
                                <tr>
                                    <th>Sr No</th>
                                    <th>Settlement ID</th>
                                    <th>User</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Reference ID</th>
                                    <th>Date</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="text-center text-muted py-5"
                                        >
                                            Loading settlements...
                                        </td>
                                    </tr>
                                ) : activeRows.length > 0 ? (
                                    activeRows.map((item, index) => (
                                        <tr
                                            key={
                                                item.id ??
                                                `${item.settlement_id || "settlement"}-${index}`
                                            }
                                        >
                                            <td className="text-nowrap">
    {(
        (activePagination?.current_page || 1) -
        1
    ) *
        (activePagination?.per_page || 10) +
        index +
        1}
</td>
                                            <td className="fw-semibold text-nowrap">
                                                {item.settlement_id || "N/A"}
                                            </td>

                                            {/* User Column */}
                                            <td>
                                                <div className="d-flex flex-column gap-1">
                                                    <div className="fw-semibold text-truncate user-name">
                                                        {item.user || "N/A"}
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                        {item.custom_user_id && (
                                                            <span className="text-muted small">
                                                                ID: {item.custom_user_id}
                                                            </span>
                                                        )}

                                                        {item.beneficiary && (
                                                            <Badge
                                                                bg="light"
                                                                text="dark"
                                                                className="border rounded-pill px-2 py-1 text-capitalize"
                                                            >
                                                                {item.beneficiary}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="fw-semibold text-nowrap">
                                                {formatAmount(item.amount)}
                                            </td>

                                            <td>
                                                <Badge
                                                    bg={badgeVariant(item.status)}
                                                    className="rounded-pill px-3 py-2 text-capitalize"
                                                >
                                                    {item.status || "N/A"}
                                                </Badge>
                                            </td>

                                            <td className="text-break">
                                                {item.reference_id ?? "N/A"}
                                            </td>

                                            <td className="text-nowrap">
                                                {formatDate(item.date)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="text-center text-muted py-5"
                                        >
                                            No settlements found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted small">
                            Showing {showingRange.start} to{" "}
                            {showingRange.end} of{" "}
                            {activePagination.total || 0} entries
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={
                                    activePagination.current_page <=
                                    1 ||
                                    loading
                                }
                                onClick={
                                    handlePreviousPage
                                }
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon
                                    icon={faChevronLeft}
                                />
                            </Button>

                            {pageItems.map(
                                (item, index) =>
                                    item === "..." ? (
                                        <span
                                            key={`dots-${index}`}
                                            className="text-muted px-1"
                                        >
                                            ...
                                        </span>
                                    ) : (
                                        <Button
                                            key={item}
                                            type="button"
                                            variant="outline-primary"
                                            disabled={loading}
                                            onClick={() =>
                                                handlePageChange(
                                                    item
                                                )
                                            }
                                            className={`course-page-btn ${item ===
                                                activePagination.current_page
                                                ? "course-page-btn-active"
                                                : ""
                                                }`}
                                        >
                                            {item}
                                        </Button>
                                    )
                            )}

                            <Button
                                type="button"
                                variant="outline-primary"
                                disabled={
                                    activePagination.current_page >=
                                    activePagination.last_page ||
                                    loading
                                }
                                onClick={
                                    handleNextPage
                                }
                                className="course-page-btn"
                            >
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                />
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <style>{`
                .settlement-toggle-group {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-wrap: nowrap;
                }

                .settlement-toggle-btn {
                    min-width: 9.5rem;
                    padding: 0.85rem 1.25rem;
                    box-shadow: none !important;
                    white-space: nowrap;
                }

                .settlement-table thead th {
                    white-space: nowrap;
                }

                .settlement-table tbody td {
                    white-space: nowrap;
                }

                .admin-settlement-export-group {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                    white-space: nowrap;
                }

                .admin-settlement-export-group .btn {
                    white-space: nowrap;
                    flex: 0 0 auto;
                }

                .admin-settlement-import-search-row {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    // margin-top: 0.75rem;
                    width: 100%;
                }

                .admin-settlement-import-group {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.75rem;
                }

                .admin-settlement-import-group .btn {
                    white-space: nowrap;
                    cursor: pointer;
                }

                .admin-settlement-search {
                    width: 100%;
                    max-width: 400px;
                }

                @media (max-width: 767.98px) {
                    .settlement-toggle-group {
                        width: auto;
                        flex-wrap: nowrap;
                    }

                    .settlement-toggle-btn {
                        width: auto;
                        flex: 1 1 0;
                        padding: 0.75rem 1rem;
                    }

                    .admin-settlement-import-search-row {
                        flex-wrap: wrap;
                        justify-content: flex-end;
                    }

                    .admin-settlement-search {
                        max-width: 100%;
                        flex: 0 0 100%;
                    }
                }

                @media (max-width: 575.98px) {
                    .admin-settlement-export-group {
                        width: 100%;
                        justify-content: flex-end;
                    }

                    .admin-settlement-import-search-row {
                        width: 100%;
                        justify-content: flex-end;
                    }

                    .admin-settlement-import-group {
                        width: 100%;
                        justify-content: flex-end;
                    }

                    .admin-settlement-search {
                        width: 100%;
                    }
                }
                .settlement-table th {
                    white-space: nowrap;
                }

                .user-name {
                    max-width: 220px;
                }

                @media (max-width: 768px) {
                    .user-name {
                        max-width: 140px;
                    }

                    .settlement-table td {
                        font-size: 0.875rem;
                    }
                }

                @media (max-width: 355.98px) {
                    .settlement-toggle-group {
                        width: 100%;
                        flex-direction: column;
                    }

                         .settlement-toggle-btn {
                        width: 100%;
                        min-width: 100%;
                    }
                        }
            `}</style>
        </>
    );
}
